import { Component, ChangeDetectorRef, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { InputFieldComponent } from '../../../custom-modules/rms-module/input-field/input-field.component';
import { SelectFieldComponent } from '../../../custom-modules/rms-module/select-field/select-field.component';
import { TextareaFieldComponent } from '../../../custom-modules/rms-module/textarea-field/textarea-field.component';
import { RmsStepperComponent } from '../../../custom-modules/rms-module/rms-stepper/rms-stepper.component';
import { RmsStepComponent } from '../../../custom-modules/rms-module/rms-stepper/rms-step.component';
import { RmsUploaderComponent, RmsUploadItem } from '../../../custom-modules/rms-module/rms-uploader/rms-uploader.component';
import { RmsPulldownRef } from '../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RtsService } from '../../../services/rts.service';

@Component({
 selector:'app-create-ticket', standalone:true,
 imports:[CommonModule,FormsModule,MatButtonModule,MatIconModule,InputFieldComponent,SelectFieldComponent,TextareaFieldComponent,RmsStepperComponent,RmsStepComponent,RmsUploaderComponent],
 templateUrl:'./create-ticket.component.html',styleUrl:'./create-ticket.component.scss'
})
export class CreateTicketComponent implements OnInit,OnDestroy {
 draft={Title:'',Description:'',TicketType:'IT',Area:'IT',Priority:'Normal',ClientRequestID:crypto.randomUUID()};
 files:RmsUploadItem[]=[];
 readonly types=[
  {label:'IT support',value:'IT',icon:'support_agent',description:'Get help with hardware, software, access, or everyday IT issues.'},
  {label:'Bug report',value:'Bug',icon:'bug_report',description:'Report something that is not working correctly in RMS or the website.'},
  {label:'Project request',value:'Project',icon:'architecture',description:'Plan a new feature or improvement for RMS or the website.'}
 ];
 readonly areas=['IT','RMS','Website'].map(value=>({label:value,value}));
 readonly priorities=['Low','Normal','High','Urgent'].map(value=>({label:value,value}));
 readonly accept='image/png,image/jpeg,image/gif,image/webp,video/mp4,video/webm,application/pdf';
 ready=false; loading=true; busy=false; error=''; progress=''; ticketID?:number; complete=false;
 uploadFailures:{name:string;message:string}[]=[];
 uploadPercent=0;
 private fileProgress=new Map<File,number>();
 private uploadKeys=new WeakMap<File,string>();
 private uploaded=new Set<File>();
 private destroyed=false;
 constructor(private api:RtsService,public ref:RmsPulldownRef<{TicketID:number}>,private cdr:ChangeDetectorRef){this.ref.canClose=()=>!this.busy;}
 @HostListener('window:beforeunload',['$event'])
 beforeUnload(event:BeforeUnloadEvent){if(this.busy){event.preventDefault();event.returnValue='';}}
 private updateProgress(file:File,percent:number){this.fileProgress.set(file,percent);const total=this.files.reduce((sum,item)=>sum+(item.size||1),0);this.uploadPercent=total?Math.floor(this.files.reduce((sum,item)=>sum+(item.size||1)*(this.fileProgress.get(item.file!)||0),0)/total):0;this.refreshView();}
 ngOnInit(){void this.connect();}
 ngOnDestroy(){this.destroyed=true;for(const item of this.files)if(item.previewUrl?.startsWith('blob:'))URL.revokeObjectURL(item.previewUrl);}
 async connect(){this.loading=true;this.error='';try{await this.api.context();this.ready=true;}catch(e:any){this.error=e?.error?.error||'Cannot connect to RTS. Please retry.';}finally{this.loading=false;this.refreshView();}}
 get valid(){return !!this.draft.Title.trim()&&this.draft.Title.length<=200&&!!this.draft.Description.trim()&&this.draft.Description.length<=20000;}
 get pending(){return this.files.filter(item=>item.file&&!this.uploaded.has(item.file));}
 chooseType(type:string){if(this.busy||this.ticketID)return;this.draft.TicketType=type;this.draft.Area=type==='IT'?'IT':'RMS';}
 // Async responses must also mark the OnPush pulldown ancestor for rendering.
 private refreshView(){if(!this.destroyed)this.cdr.markForCheck();}
 fileKey(file:File){let key=this.uploadKeys.get(file);if(!key){key=crypto.randomUUID();this.uploadKeys.set(file,key);}return key;}
 async submit(){
  if(this.busy||!this.valid||!this.ready||this.complete)return;
  this.busy=true;this.error='';this.uploadFailures=[];
  try{
   if(!this.ticketID){
    this.progress='Creating your ticket…';this.refreshView();
    const result=await this.api.create(this.draft);
    this.ticketID=result.TicketID;
    this.api.ticketChanged.next(this.ticketID);
   }
   // If a response is lost, the same request/upload keys make retry safe.
   const pending=this.pending;
   for(const [index,item] of pending.entries()){
    if(this.destroyed)break;
    this.progress='Uploading '+(index+1)+' of '+pending.length+': '+item.name;this.refreshView();
    try{
     await this.api.upload(this.ticketID,item.file!,'Evidence',undefined,this.fileKey(item.file!),percent=>this.updateProgress(item.file!,percent));
     this.uploaded.add(item.file!);this.updateProgress(item.file!,100);
    }catch(e:any){
     this.uploadFailures.push({name:item.name,message:e?.error?.error||'Upload failed. Please retry.'});
    }
   }
   this.complete=this.pending.length===0;
   if(this.uploadFailures.length)this.error='Your ticket is saved. Some attachments need another attempt; retry uploads below without creating another ticket.';
   this.api.ticketChanged.next(this.ticketID);
  }catch(e:any){this.error=e?.error?.error||'The request could not be confirmed. Retry safely to check or finish the same ticket.';}
  finally{this.busy=false;this.progress='';this.refreshView();}
 }
 close(){if(!this.busy)this.ref.close(this.ticketID?{TicketID:this.ticketID}:undefined);}
}
